import { Component, computed, ElementRef, inject, QueryList, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CanvasService } from '../../services/canvas.service';
import { RGBA } from '../../models/canvas-object.model';

@Component({
    selector: 'app-properties-panel',
    imports: [CommonModule, FormsModule],
    templateUrl: './properties-panel.component.html',
    styleUrl: './properties-panel.component.scss'
})
export class PropertiesPanelComponent {
    @ViewChildren('svgLayerItem') svgLayerItems?: QueryList<ElementRef<HTMLElement>>;
    private canvasService = inject(CanvasService);
    readonly selectedObjects = this.canvasService.selectedObjects;
    readonly selectedObject = computed(() => {
        const selected = this.selectedObjects();
        return selected.length === 1 ? selected[0] : null;
    });
    readonly selectedCount = computed(() => this.selectedObjects().length);

    readonly googleFonts = [
        'Roboto', 'Open Sans', 'Lato', 'Montserrat', 'Oswald',
        'Raleway', 'PT Sans', 'Merriweather', 'Nunito', 'Playfair Display',
        'Ubuntu', 'Poppins', 'Inter', 'Mulish', 'Work Sans',
        'Noto Sans', 'Quicksand', 'Karla', 'Rubik', 'Source Sans Pro',
        'Oxygen', 'Arimo', 'Cabin', 'Dosis', 'PT Serif',
        'Crimson Text', 'Indie Flower', 'Dancing Script', 'Pacifico', 'Lobster',
        'Bebas Neue', 'Abel', 'Anton', 'Archivo', 'Bitter',
        'Comfortaa', 'Exo', 'Fjalla One', 'Hind', 'Josefin Sans',
        'Arial', 'Helvetica', 'Times New Roman', 'Courier New', 'Georgia', 'Verdana'
    ];

    constructor() {
        // Load Google Fonts dynamically
        this.loadGoogleFonts();
    }

    private loadGoogleFonts(): void {
        // Create link element for Google Fonts
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        const fontsToLoad = this.googleFonts
            .filter(f => !['Arial', 'Helvetica', 'Times New Roman', 'Courier New', 'Georgia', 'Verdana'].includes(f))
            .map(f => f.replace(/ /g, '+'))
            .join('&family=');
        link.href = `https://fonts.googleapis.com/css2?family=${fontsToLoad}&display=swap`;
        document.head.appendChild(link);
    }

    getSVGLayers(obj: any): any[] {
        return obj.layers || [];
    }

    hasSVGLayers(obj: any): boolean {
        return obj.layers && obj.layers.length > 0;
    }

    getSVGLayerFill(layer: any): string | undefined {
        return layer?.fill ?? layer?.originalFill;
    }

    getSVGLayerStroke(layer: any): string | undefined {
        return layer?.stroke ?? layer?.originalStroke;
    }

    getUniqueColors(obj: any): string[] {
        if (!obj.layers) return [];
        const colors = new Set<string>();
        obj.layers.forEach((layer: any) => {
            const fill = this.getSVGLayerFill(layer);
            const stroke = this.getSVGLayerStroke(layer);
            if (fill && fill !== 'none') colors.add(fill);
            if (stroke && stroke !== 'none') colors.add(stroke);
        });
        return Array.from(colors);
    }

    updateAllLayersWithColor(objectId: string, oldColor: string, event: Event): void {
        const newColor = (event.target as HTMLInputElement).value;
        const obj = this.selectedObject();
        if (!obj || obj.type !== 'svg' || !obj.layers) return;

        const oldColorHex = this.svgColorToHex(oldColor);

        obj.layers.forEach((layer: any) => {
            const fill = this.getSVGLayerFill(layer);
            const stroke = this.getSVGLayerStroke(layer);

            if (fill && this.svgColorToHex(fill) === oldColorHex) {
                this.canvasService.updateSVGLayer(objectId, layer.id, newColor, undefined);
            }
            if (stroke && this.svgColorToHex(stroke) === oldColorHex) {
                this.canvasService.updateSVGLayer(objectId, layer.id, undefined, newColor);
            }
        });

        this.triggerRerender();
    }

    private scrollToSelectedLayer(layerId: string): void {
        const items = this.svgLayerItems?.toArray() ?? [];
        const target = items.find(item => item.nativeElement.dataset['layerId'] === layerId);
        if (!target) return;

        target.nativeElement.scrollIntoView({ block: 'nearest' });
    }

    updatePosition(axis: 'x' | 'y', event: Event): void {
        const value = parseFloat((event.target as HTMLInputElement).value);
        const obj = this.selectedObject();
        if (!obj) return;

        this.canvasService.updateObject(obj.id, {
            position: {
                ...obj.position,
                [axis]: value
            }
        });
    }

    updateSize(dimension: 'width' | 'height', event: Event): void {
        const value = parseFloat((event.target as HTMLInputElement).value);
        const obj = this.selectedObject();
        if (!obj) return;

        this.canvasService.updateObject(obj.id, {
            size: {
                ...obj.size,
                [dimension]: Math.max(1, value)
            }
        });
    }

    updateRotation(event: Event): void {
        const value = parseFloat((event.target as HTMLInputElement).value);
        const obj = this.selectedObject();
        if (!obj) return;

        this.canvasService.updateObject(obj.id, {
            transform: {
                ...obj.transform,
                rotation: value
            }
        });
    }

    updateScale(axis: 'scaleX' | 'scaleY', event: Event): void {
        const value = parseFloat((event.target as HTMLInputElement).value);
        const obj = this.selectedObject();
        if (!obj) return;

        this.canvasService.updateObject(obj.id, {
            transform: {
                ...obj.transform,
                [axis]: value
            }
        });
    }

    updateOpacity(event: Event): void {
        const value = parseFloat((event.target as HTMLInputElement).value);
        const obj = this.selectedObject();
        if (!obj) return;

        this.canvasService.updateObject(obj.id, { opacity: value });
    }

    getCornerRadius(obj: any): number {
        return obj?.cornerRadius || 0;
    }

    updateCornerRadius(event: Event): void {
        const value = parseFloat((event.target as HTMLInputElement).value);
        const obj = this.selectedObject();
        if (!obj) return;

        this.canvasService.updateObject(obj.id, { cornerRadius: value } as any);
    }

    updateTextContent(value: string): void {
        const obj = this.selectedObject();
        if (!obj || obj.type !== 'text') return;

        this.canvasService.updateObject(obj.id, { content: value } as any);
    }

    updateTextStyle(property: string, event: Event): void {
        const target = event.target as HTMLInputElement | HTMLSelectElement;
        let value: any = target.value;

        if (property === 'fontSize') {
            value = parseFloat(value);
        }

        const obj = this.selectedObject();
        if (!obj || obj.type !== 'text') return;

        this.canvasService.updateObject(obj.id, {
            textStyle: {
                ...obj.textStyle,
                [property]: value
            }
        } as any);
    }

    updateFillColor(event: Event): void {
        const hex = (event.target as HTMLInputElement).value;
        const obj = this.selectedObject();
        if (!obj || !obj.fill) return;

        const rgb = this.hexToRgb(hex);
        this.canvasService.updateObject(obj.id, {
            fill: { ...rgb, a: obj.fill.a }
        });
    }

    updateFillAlpha(event: Event): void {
        const value = parseFloat((event.target as HTMLInputElement).value);
        const obj = this.selectedObject();
        if (!obj || !obj.fill) return;

        this.canvasService.updateObject(obj.id, {
            fill: { ...obj.fill, a: value }
        });
    }

    updateStrokeColor(event: Event): void {
        const hex = (event.target as HTMLInputElement).value;
        const obj = this.selectedObject();
        if (!obj || !obj.stroke) return;

        const rgb = this.hexToRgb(hex);
        this.canvasService.updateObject(obj.id, {
            stroke: { ...obj.stroke, color: { ...rgb, a: obj.stroke.color.a } }
        });
    }

    updateStrokeWidth(event: Event): void {
        const value = parseFloat((event.target as HTMLInputElement).value);
        const obj = this.selectedObject();
        if (!obj || !obj.stroke) return;

        this.canvasService.updateObject(obj.id, {
            stroke: { ...obj.stroke, width: Math.max(0, value) }
        });
    }

    addFill(): void {
        const obj = this.selectedObject();
        if (!obj) return;

        this.canvasService.updateObject(obj.id, {
            fill: { r: 100, g: 150, b: 255, a: 1 }
        });
    }

    addStroke(): void {
        const obj = this.selectedObject();
        if (!obj) return;

        this.canvasService.updateObject(obj.id, {
            stroke: {
                color: { r: 50, g: 100, b: 200, a: 1 },
                width: 2
            }
        });
    }

    updateSVGLayerFill(objectId: string, layerId: string, event: Event): void {
        const color = (event.target as HTMLInputElement).value;
        this.canvasService.updateSVGLayer(objectId, layerId, color, undefined);
        this.triggerRerender();
    }

    updateSVGLayerStroke(objectId: string, layerId: string, event: Event): void {
        const color = (event.target as HTMLInputElement).value;
        this.canvasService.updateSVGLayer(objectId, layerId, undefined, color);
        this.triggerRerender();
    }

    private triggerRerender(): void {
        // Trigger re-render by creating a custom event that canvas can listen to
        window.dispatchEvent(new CustomEvent('canvas-rerender', { detail: { clearCache: true } }));
    }

    bringToFront(): void {
        const obj = this.selectedObject();
        if (!obj) return;

        this.canvasService.bringToFront([obj.id]);
    }

    sendToBack(): void {
        const obj = this.selectedObject();
        if (!obj) return;

        this.canvasService.sendToBack([obj.id]);
    }

    toggleVisibility(): void {
        const obj = this.selectedObject();
        if (!obj) return;

        this.canvasService.updateObject(obj.id, { visible: !obj.visible });
    }

    toggleLocked(): void {
        const obj = this.selectedObject();
        if (!obj) return;

        this.canvasService.updateObject(obj.id, { locked: !obj.locked });
    }

    alignLeft(): void {
        this.canvasService.alignSelected('left');
    }

    alignCenterHorizontal(): void {
        this.canvasService.alignSelected('h-center');
    }

    alignRight(): void {
        this.canvasService.alignSelected('right');
    }

    alignTop(): void {
        this.canvasService.alignSelected('top');
    }

    alignCenterVertical(): void {
        this.canvasService.alignSelected('v-center');
    }

    alignBottom(): void {
        this.canvasService.alignSelected('bottom');
    }

    distributeHorizontal(): void {
        this.canvasService.distributeSelected('horizontal');
    }

    distributeVertical(): void {
        this.canvasService.distributeSelected('vertical');
    }

    tidyUp(): void {
        this.canvasService.tidyUpSelected();
    }

    rgbaToHex(rgba: RGBA): string {
        const r = rgba.r.toString(16).padStart(2, '0');
        const g = rgba.g.toString(16).padStart(2, '0');
        const b = rgba.b.toString(16).padStart(2, '0');
        return `#${r}${g}${b}`;
    }

    svgColorToHex(color?: string): string {
        if (!color) return '#000000';
        const trimmed = color.trim();
        if (trimmed === 'none') return '#000000';

        if (trimmed.startsWith('#')) {
            if (trimmed.length === 4) {
                const r = trimmed[1];
                const g = trimmed[2];
                const b = trimmed[3];
                return `#${r}${r}${g}${g}${b}${b}`;
            }
            if (trimmed.length >= 7) {
                return trimmed.slice(0, 7);
            }
        }

        const rgbMatch = trimmed.match(/rgba?\(([^)]+)\)/i);
        if (rgbMatch) {
            const parts = rgbMatch[1].match(/-?\d*\.?\d+%?/g) || [];
            if (parts.length >= 3) {
                const toByte = (value: string): number => {
                    if (value.endsWith('%')) {
                        const pct = Math.max(0, Math.min(100, parseFloat(value)));
                        return Math.round((pct / 100) * 255);
                    }
                    return Math.max(0, Math.min(255, Math.round(parseFloat(value))));
                };
                const rPart = parts[0];
                const gPart = parts[1];
                const bPart = parts[2];
                if (!rPart || !gPart || !bPart) return '#000000';
                const r = toByte(rPart);
                const g = toByte(gPart);
                const b = toByte(bPart);
                return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
            }
        }

        const temp = document.createElement('span');
        temp.style.color = trimmed;
        document.body.appendChild(temp);
        const computed = getComputedStyle(temp).color;
        document.body.removeChild(temp);
        const computedMatch = computed.match(/rgba?\(([^)]+)\)/i);
        if (computedMatch) {
            const parts = computedMatch[1].match(/-?\d*\.?\d+%?/g) || [];
            if (parts.length >= 3) {
                const toByte = (value: string): number => {
                    if (value.endsWith('%')) {
                        const pct = Math.max(0, Math.min(100, parseFloat(value)));
                        return Math.round((pct / 100) * 255);
                    }
                    return Math.max(0, Math.min(255, Math.round(parseFloat(value))));
                };
                const rPart = parts[0];
                const gPart = parts[1];
                const bPart = parts[2];
                if (!rPart || !gPart || !bPart) return '#000000';
                const r = toByte(rPart);
                const g = toByte(gPart);
                const b = toByte(bPart);
                return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
            }
        }

        return '#000000';
    }

    hexToRgb(hex: string): { r: number; g: number; b: number } {
        const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
        return result
            ? {
                r: parseInt(result[1], 16),
                g: parseInt(result[2], 16),
                b: parseInt(result[3], 16)
            }
            : { r: 0, g: 0, b: 0 };
    }
}
